/**
 * The chronological dot grid shared by the Field page's hub and sample cards,
 * plus the machinery that makes a hub and its samples scroll as one.
 *
 * A hub and the samples wired to it are driven by the same acquisition
 * schedule, so their slot lists are identical. Rendering them through the same
 * <DotStrip> — same fixed label gutter, same day columns — puts every
 * acquisition at the same x across the linked cards, and a shared
 * ScrollSync keeps them at the same offset while the user pans.
 */
import { useEffect, useMemo, useRef } from 'react';
import { DotStatus } from '@/services/field.service';

export const DOT_COLOR: Record<DotStatus, string> = {
    green: '#74c69d', // present, non-empty
    yellow: '#e0b341', // present but 0 bytes
    red: '#d9534f', // missing
    blue: '#5b9bd5', // unplanned but accounted for: extra file, config wake-up
    purple: '#a97bd8', // unplanned and unexplained: a normal-mode wake-up
    none: 'transparent', // nothing here — holds the column open
};

/** Width of the label gutter on a sample card, in px. */
const LABEL_GUTTER_PX = 48;

/**
 * The hub/sample tree geometry, in px from the left edge of a hub group.
 *
 * The hub card is the full width of the group and its samples are inset, with a
 * rail running down between them. The hub's dot strip widens its label gutter by
 * exactly the inset, so both grids still begin on the same pixel and the two
 * scroll viewports come out the same width — which is what keeps the shared
 * scrolling from fighting itself.
 */
export const RAIL_X_PX = 12;
export const RAIL_WIDTH_PX = 2;
export const SAMPLE_INDENT_PX = 34;
/** Length of the horizontal tick from the rail to a sample card. */
export const TICK_PX = SAMPLE_INDENT_PX - RAIL_X_PX - RAIL_WIDTH_PX;
/** Colour of the rail and its ticks — structure, not signal. */
export const RAIL_COLOR = '#2a323a';

export function Dot({ color, title }: { color: DotStatus; title?: string }) {
    return (
        <span
            // A blank placeholder has nothing to say on hover.
            title={color === 'none' ? undefined : title}
            className="h-2 w-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: DOT_COLOR[color] }}
        />
    );
}

// ---- shared horizontal scrolling -----------------------------------------

export interface ScrollSync {
    /** Wire a scroll viewport into the group; returns the unregister callback. */
    register: (el: HTMLDivElement) => () => void;
    /**
     * Pan the whole group by `delta` px. Returns whether the group owns
     * horizontal movement at all — true even once it has hit an edge, so the
     * wheel stays captured instead of spilling into the page.
     */
    scrollBy: (delta: number) => boolean;
}

/** A scroll group. Create one per set of cards that should move together. */
export function useScrollSync(): ScrollSync {
    return useMemo(createScrollSync, []);
}

function createScrollSync(): ScrollSync {
    const viewports = new Set<HTMLDivElement>();
    /** Shared offset; null until the first viewport pins the group. */
    let left: number | null = null;

    const maxOf = (el: HTMLDivElement) =>
        Math.max(0, el.scrollWidth - el.clientWidth);

    /** Push the shared offset onto every viewport but the one that moved. */
    const apply = (source: HTMLDivElement | null) => {
        if (left === null) return;
        for (const el of viewports) {
            if (el === source) continue;
            const value = Math.min(left, maxOf(el));
            // Their own scroll events then see the offset they already hold and
            // stop there, so this doesn't bounce back and forth.
            if (el.scrollLeft !== value) el.scrollLeft = value;
        }
    };

    return {
        register(el) {
            const onScroll = () => {
                if (el.scrollLeft === left) return;
                left = el.scrollLeft;
                apply(el);
            };
            viewports.add(el);
            el.addEventListener('scroll', onScroll);
            // The first card to mount pins the group hard right, so the most
            // recent acquisitions are the ones in view.
            if (left === null) left = maxOf(el);
            el.scrollLeft = Math.min(left, maxOf(el));

            return () => {
                el.removeEventListener('scroll', onScroll);
                viewports.delete(el);
            };
        },

        scrollBy(delta) {
            const [first] = viewports;
            if (!first || left === null) return false;
            const max = maxOf(first);
            // Nothing to pan (the grid fits): the wheel belongs to the page.
            if (max === 0) return false;
            const next = Math.max(0, Math.min(max, left + delta));
            if (next !== left) {
                left = next;
                apply(null);
            }
            return true;
        },
    };
}

/**
 * Turn the wheel into horizontal panning of `sync` anywhere over the returned
 * element, so the cursor need only be somewhere on the card. The card keeps the
 * wheel for as long as it has a grid to pan — including once that grid is at an
 * edge — so the page never starts scrolling under the cursor. Move off the card
 * to scroll the page.
 */
export function useWheelPan<T extends HTMLElement>(sync: ScrollSync) {
    const ref = useRef<T>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const onWheel = (event: WheelEvent) => {
            const raw =
                Math.abs(event.deltaX) > Math.abs(event.deltaY)
                    ? event.deltaX
                    : event.deltaY;
            if (raw === 0) return;
            // Firefox reports lines (1) or pages (2) rather than pixels.
            const scale =
                event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? 100 : 1;
            if (sync.scrollBy(raw * scale)) event.preventDefault();
        };

        // Non-passive, since React's own onWheel can't preventDefault.
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [sync]);

    return ref;
}

// ---- the grid ------------------------------------------------------------

/** One row of dots: its gutter label and how to colour a slot. */
export interface DotRow<T> {
    label: string;
    dot: (slot: T) => { color: DotStatus; title: string };
}

/**
 * The day-by-day dot grid of one card. Every card in a `sync` group must be
 * given the same slots for their columns to line up.
 */
export function DotStrip<T extends { time: string }>({
    sync,
    slots,
    rows,
    indentPx = 0,
}: {
    sync: ScrollSync;
    slots: T[];
    rows: DotRow<T>[];
    /**
     * Extra label-gutter width, for a card that sits further left than the
     * others in its group. The hub card passes SAMPLE_INDENT_PX so its dots
     * still land under the samples' own.
     */
    indentPx?: number;
}) {
    const days = groupByDay(slots);
    const viewport = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = viewport.current;
        if (!el) return;
        return sync.register(el);
    }, [sync, slots.length]);

    return (
        <div className="flex">
            <div
                className="flex flex-col flex-shrink-0 pr-3"
                style={{ width: LABEL_GUTTER_PX + indentPx }}
            >
                {rows.map((row) => (
                    <div
                        key={row.label}
                        className="h-3.5 flex items-center text-[9px] text-[#6b7480]"
                    >
                        {row.label}
                    </div>
                ))}
                {/* spacer aligning with the day-label footer */}
                <div className="h-4 mt-1" />
            </div>
            <div ref={viewport} className="overflow-x-auto">
                <div className="flex">
                    {days.map((day) => (
                        <div
                            key={day.key}
                            className="flex flex-col px-1.5 border-l border-[#1e242a] first:border-l-0 first:pl-0"
                        >
                            {rows.map((row) => (
                                <div
                                    key={row.label}
                                    className="flex items-center gap-[3px] h-3.5"
                                >
                                    {day.slots.map((slot) => {
                                        const { color, title } = row.dot(slot);
                                        return (
                                            <Dot
                                                key={slot.time}
                                                color={color}
                                                title={title}
                                            />
                                        );
                                    })}
                                </div>
                            ))}
                            <div className="h-4 mt-1 text-[9px] text-[#6b7480] text-center whitespace-nowrap">
                                {day.label}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

interface DayGroup<T> {
    key: string;
    label: string;
    slots: T[];
}

/** Group slots by UTC calendar day, preserving chronological order. */
function groupByDay<T extends { time: string }>(slots: T[]): DayGroup<T>[] {
    const groups: DayGroup<T>[] = [];
    let current: DayGroup<T> | null = null;
    for (const slot of slots) {
        const key = slot.time.slice(0, 10); // YYYY-MM-DD (UTC)
        if (!current || current.key !== key) {
            current = { key, label: dayLabel(key), slots: [] };
            groups.push(current);
        }
        current.slots.push(slot);
    }
    return groups;
}

function dayLabel(isoDay: string): string {
    const [, month, day] = isoDay.split('-');
    return `${day}/${month}`;
}
